import { FileTypes, IOption } from '@gcl/react-core';
import { FETCH_QUERIES_NAME } from '@common/constants';
import {
  useFetchGlobalRolesByUserProfileId,
  useFetchUserProfile,
  useUpdateGlobalRoles,
  useUpdateUserProfile,
} from '@userProfile/userProfile.queries';
import { useFetchGlobalRoleOptions, useFetchProjectRoleOptions } from '@role/projectRole/projectRole.queries';
import { AxiosErrorResponse } from '@core/error.models';
import { IRoleOption } from '@role/role.models';
import { UseQueryResult } from '@tanstack/react-query';
import { IUseFetchOptionsParams } from '@common/api.models';
import {
  IAdminPageFieldConfig,
  IAdminPageFieldConfigImage,
  IAdminPageFieldConfigSelect,
  IAdminPageItemConfig,
} from './admin.models';
import {
  useAdminUpdateProjectUserProfileAsync,
  useAnnouncementFileUpload,
  useCreateAnnouncement,
  useCreatePermission,
  useCreateProjectRole,
  useCreateRole,
  useCreateRolePermission,
  useCreateTraining,
  useFetchAdminSignature,
  useFetchAdminTeamInfo,
  useFetchAnnouncement,
  useFetchICECert,
  useFetchProjectUserProfileByCompany,
  useFetchTraining,
  useUpdateAdminSignature,
  useReplaceDummyUserProfiles,
  useUpdateAdminTeamInfo,
  useUpdateAnnouncement,
  useUpdateICECert,
  useUpdateTraining,
  useUpdateUserProfilesSyncNoAccountUsersByProjectNo,
  usePostAdminSignature,
} from './admin.queries';
import { uploadFileSuccessCallback } from './admin.hooks';
import { AdminPageSyncTrainingRecordButton } from './AdminPageSyncTrainingRecordButton';

export enum EAdminPageMainFormKey {
  PROJECT_NO = 'projectNo',
  ID = 'id',
  IS_ADD = 'isAdd',
}

export enum EAdminPageFormMode {
  ADD = 'add',
  UPDATE = 'update',
}

export const FILE_UPLOAD_URL_FIELD_NAME = 'Url';

export enum EAdminPageType {
  TEAM_INFO = 'teamInfo',
  ANNOUNCEMENT = 'announcement',
  UPLOAD_FILE = 'uploadFile',
  GLOBAL_ROLES = 'globalRoles',
  SYNC_NO_ACCOUNT_USERS = 'syncNoAccountUsers',
  USER_PROFILE = 'userProfile',
  TRAINING_RECORD = 'trainingRecord',
  ICE_CERT = 'iceCert',
  PROJECT_USER_PROFILE_WITH_ROLES_BY_COMPANY = 'projectUserProfileWithRolesByCompany',
  SIGNATURE = 'signature',
  UPDATE_DUMMY_USER_PROFILES = 'updateDummyUserProfiles',
  ROLE = 'role',
  PERMISSION = 'permission',
  PROJECT_ROLE = 'projectRole',
  ROLE_PERMISSION = 'rolePermission',
}
export enum EAdminPageFieldType {
  TEXT_INPUT = 'textInput',
  TEXT_AREA = 'textArea',
  NUMBER = 'number',
  BOOLEAN = 'boolean',
  DATE = 'date',
  LABEL_DATE = 'labelDate', // for display
  LABEL = 'label', // title
  FILE_UPLOAD_SINGLE = 'fileUploadSingle',
  SEPARATOR = 'separator',
  CHECKBOX_GROUP = 'checkboxGroup',
  GROUP = 'group',
  IMAGE = 'image',
  NONE = 'none',
}
export const addRolePermissionFormConfig: IAdminPageFieldConfig[] = [
  { label: 'Role ID', fieldName: 'roleId', fieldType: EAdminPageFieldType.NUMBER, required: true },
  { label: 'Permission ID', fieldName: 'permissionId', fieldType: EAdminPageFieldType.NUMBER, required: true },
];
export const addProjectRoleFormConfig: IAdminPageFieldConfig[] = [
  { label: 'Project ID', fieldName: 'projectId', fieldType: EAdminPageFieldType.NUMBER, required: true },
  { label: 'Role ID', fieldName: 'roleId', fieldType: EAdminPageFieldType.NUMBER, required: true },
  { label: 'Company Type ID', fieldName: 'companyTypeId', fieldType: EAdminPageFieldType.NUMBER, required: true },
  {
    label: 'Organization Type ID',
    fieldName: 'organizationTypeId',
    fieldType: EAdminPageFieldType.NUMBER,
    required: true,
  },
];

export const addRoleFormConfig: IAdminPageFieldConfig[] = [
  { label: 'Name', fieldName: 'name', fieldType: EAdminPageFieldType.TEXT_INPUT, required: true },
  { label: 'Company Type ID', fieldName: 'companyTypeId', fieldType: EAdminPageFieldType.NUMBER, required: true },
  {
    label: 'Organization Type ID',
    fieldName: 'organizationTypeId',
    fieldType: EAdminPageFieldType.NUMBER,
    required: true,
  },
  { label: 'Sort Order', fieldName: 'sortOrder', fieldType: EAdminPageFieldType.NUMBER, required: true },
  { label: 'Keyword', fieldName: 'keyword', fieldType: EAdminPageFieldType.TEXT_INPUT },
  { label: 'type', fieldName: 'type', fieldType: EAdminPageFieldType.TEXT_INPUT, required: true },
  { label: 'Is Deleted', fieldName: 'isDeleted', fieldType: EAdminPageFieldType.BOOLEAN },
  { label: 'Is Read Only', fieldName: 'isReadOnly', fieldType: EAdminPageFieldType.BOOLEAN },
  { label: 'Is Project Role', fieldName: 'isProjectRole', fieldType: EAdminPageFieldType.BOOLEAN },
];

export const addPermissionFormConfig: IAdminPageFieldConfig[] = [
  { label: 'Name', fieldName: 'name', fieldType: EAdminPageFieldType.TEXT_INPUT, required: true },
  { label: 'Item Code', fieldName: 'itemCode', fieldType: EAdminPageFieldType.TEXT_INPUT, required: true },
  { label: 'Remark', fieldName: 'remark', fieldType: EAdminPageFieldType.TEXT_INPUT, required: true },
];

export const updateSignatureConfig: Array<IAdminPageFieldConfig | IAdminPageFieldConfigImage> = [
  {
    label: 'ID',
    fieldName: 'id',
    fieldType: {
      add: EAdminPageFieldType.NONE,
      update: EAdminPageFieldType.LABEL,
    },
  },
  {
    label: 'User Profile ID',
    fieldName: 'userProfileId',
    fieldType: { add: EAdminPageFieldType.NUMBER, update: EAdminPageFieldType.LABEL },
  },
  { label: 'File Name', fieldName: 'fileName', fieldType: EAdminPageFieldType.LABEL },
  {
    label: 'Signature',
    fieldName: 'fileContent',
    fieldType: EAdminPageFieldType.IMAGE,
    mimeTypeFieldName: 'mime',
    fileNameFieldName: 'fileName',
  },
  {
    label: 'Row Version',
    fieldName: 'rowVersion',
    fieldType: {
      add: EAdminPageFieldType.NONE,
      update: EAdminPageFieldType.LABEL,
    },
  },
];
export const updateDummyUserProfilesFormConfig: IAdminPageFieldConfig[] = [
  { label: 'User Profile ID', fieldName: 'userProfileId', dataFieldName: 'id', fieldType: EAdminPageFieldType.LABEL },
  { label: 'UPN', fieldName: 'upn', fieldType: EAdminPageFieldType.LABEL },
  { label: 'Email', fieldName: 'email', dataFieldName: 'id', fieldType: EAdminPageFieldType.LABEL },
  { label: 'Display Name', fieldName: 'displayName', dataFieldName: 'id', fieldType: EAdminPageFieldType.LABEL },
  {
    label: 'User Profile IDs to Update (separate IDs with ",", eg: "10001,10002,10003")',
    fieldName: 'userProfileIdsToUpdate',
    fieldType: EAdminPageFieldType.TEXT_INPUT,
    convertFn: (value: string) => {
      return value.split(',').map((s) => parseInt(s, 10));
    },
  },
];

export const updateProjectUserRoleFetchFormConfig: IAdminPageFieldConfig[] = [
  { label: 'Company ID', fieldName: 'companyId', fieldType: EAdminPageFieldType.NUMBER },
  { label: 'Project ID', fieldName: 'projectId', fieldType: EAdminPageFieldType.NUMBER },
];
export const updateProjectUserRoleConfig: Array<IAdminPageFieldConfig | IAdminPageFieldConfigSelect> = [
  { label: 'ProjectCompany ID', fieldName: 'company.id', fieldType: EAdminPageFieldType.LABEL },
  { label: 'Company Name', fieldName: 'company.name', fieldType: EAdminPageFieldType.LABEL },
  { label: '', fieldName: '', fieldType: EAdminPageFieldType.SEPARATOR },
  { label: 'User Profile ID', fieldName: 'userProfile.id', fieldType: EAdminPageFieldType.LABEL },
  { label: 'Project User Profile ID', fieldName: 'id', fieldType: EAdminPageFieldType.LABEL },
  { label: 'UID', fieldName: 'userProfile.uid', fieldType: EAdminPageFieldType.LABEL },
  { label: 'Email', fieldName: 'userProfile.email', fieldType: EAdminPageFieldType.LABEL },
  { label: 'UPN', fieldName: 'userProfile.upn', fieldType: EAdminPageFieldType.LABEL },
  { label: 'First Name', fieldName: 'userProfile.firstName', fieldType: EAdminPageFieldType.LABEL },
  { label: 'Last Name', fieldName: 'userProfile.lastName', fieldType: EAdminPageFieldType.LABEL },
  { label: 'Display Name', fieldName: 'userProfile.displayName', fieldType: EAdminPageFieldType.LABEL },
  { label: '', fieldName: '', fieldType: EAdminPageFieldType.SEPARATOR },
  {
    label: 'Roles',
    fieldName: 'roles',
    fieldType: EAdminPageFieldType.CHECKBOX_GROUP,
    optionGetProp: { shouldConvertOptions: true },
    optionGetFn: useFetchProjectRoleOptions, // useFetchGlobalRoleOptions({}),
    optionGetFnGetRequest: (idFormValues: { [key: string]: any }, itemFormValues: { [key: string]: any }) => {
      return {
        companyTypeId: itemFormValues.company?.companyTypeId,
        projectId: idFormValues.projectId,
        organizationTypeId: itemFormValues.company?.organizationTypeId,
        shouldIncludeHidden: true,
      };
    },
    gridSm: 12,
  },
];

export const addOrUpdateTrainingRecordConfig: IAdminPageFieldConfig[] = [
  {
    label: 'ID',
    fieldName: 'id',
    fieldType: {
      add: EAdminPageFieldType.NONE,
      update: EAdminPageFieldType.LABEL,
    },
  },
  { label: 'User Profile ID', fieldName: 'userProfileId', fieldType: EAdminPageFieldType.NUMBER, required: true },
  { label: 'Course Code', fieldName: 'courseCode', fieldType: EAdminPageFieldType.TEXT_INPUT, required: true },
  { label: 'Completion Date', fieldName: 'completionDate', fieldType: EAdminPageFieldType.DATE },
  {
    label: 'Row version',
    fieldName: 'rowVersion',
    fieldType: {
      add: EAdminPageFieldType.NONE,
      update: EAdminPageFieldType.LABEL,
    },
  },
];

export const userProfileConfig: IAdminPageFieldConfig[] = [
  { label: 'ID', fieldName: 'id', fieldType: EAdminPageFieldType.LABEL },
  { label: 'UID', fieldName: 'uid', fieldType: EAdminPageFieldType.TEXT_INPUT },

  { label: 'Email', fieldName: 'email', fieldType: EAdminPageFieldType.TEXT_INPUT },
  { label: 'Upn', fieldName: 'upn', fieldType: EAdminPageFieldType.TEXT_INPUT },
  { label: 'First name', fieldName: 'firstName', fieldType: EAdminPageFieldType.TEXT_INPUT },
  { label: 'Last name', fieldName: 'lastName', fieldType: EAdminPageFieldType.TEXT_INPUT },
  { label: 'Display name', fieldName: 'displayName', fieldType: EAdminPageFieldType.TEXT_INPUT },
  { label: 'Title', fieldName: 'title', fieldType: EAdminPageFieldType.TEXT_INPUT },
  { label: 'Tel.', fieldName: 'tel', fieldType: EAdminPageFieldType.TEXT_INPUT },
  { label: 'Fax', fieldName: 'fax', fieldType: EAdminPageFieldType.TEXT_INPUT },
  { label: 'Last Project ID', fieldName: 'lastProjectId', fieldType: EAdminPageFieldType.NUMBER },
  { label: 'Last Login Time', fieldName: 'lastLoginTime', fieldType: EAdminPageFieldType.LABEL },
  { label: 'Is Locked', fieldName: 'isLocked', fieldType: EAdminPageFieldType.BOOLEAN },
  {
    label: 'Default right panel closed?',
    fieldName: 'defaultRightPanelClosed',
    fieldType: EAdminPageFieldType.BOOLEAN,
  },
  { label: 'Login Time', fieldName: 'loginTime', fieldType: EAdminPageFieldType.LABEL },
  { label: 'TW Approval Type', fieldName: 'twApprovalType', fieldType: EAdminPageFieldType.TEXT_INPUT },
  { label: 'Qualification', fieldName: 'qualification', fieldType: EAdminPageFieldType.TEXT_INPUT },
  { label: 'Max Team Board Master Id', fieldName: 'maxTeamBoardMasterId', fieldType: EAdminPageFieldType.NUMBER },
  { label: 'GEMS Lead?', fieldName: 'gemsLead', fieldType: EAdminPageFieldType.BOOLEAN },
  { label: 'Ekpid', fieldName: 'ekpid', fieldType: EAdminPageFieldType.TEXT_INPUT },
  { label: 'Employee Id', fieldName: 'employeeId', fieldType: EAdminPageFieldType.TEXT_INPUT },
  { label: 'Is External', fieldName: 'isExternal', fieldType: EAdminPageFieldType.BOOLEAN },
  { label: 'Is account created', fieldName: 'isAccountCreated', fieldType: EAdminPageFieldType.BOOLEAN },
  { label: 'Row version', fieldName: 'rowVersion', fieldType: EAdminPageFieldType.LABEL },
];

export const syncNoAccountUsersConfig: IAdminPageFieldConfig[] = [
  { label: 'Project ID', fieldName: 'project.id', fieldType: EAdminPageFieldType.LABEL },
  { label: 'Project No.', fieldName: 'project.projectNo', fieldType: EAdminPageFieldType.LABEL },
  { label: 'Project Name', fieldName: 'project.projectName', fieldType: EAdminPageFieldType.LABEL },
  { label: '', fieldName: '', fieldType: EAdminPageFieldType.SEPARATOR },
  {
    label: 'Updated Profiles',
    fieldName: 'profiles',
    fieldType: EAdminPageFieldType.GROUP,
    groupConfig: [
      { label: 'User profile ID', fieldName: 'id', fieldType: EAdminPageFieldType.LABEL },
      { label: 'Email', fieldName: 'email', fieldType: EAdminPageFieldType.LABEL },
      { label: 'Upn', fieldName: 'upn', fieldType: EAdminPageFieldType.LABEL },
      { label: 'First name', fieldName: 'firstName', fieldType: EAdminPageFieldType.LABEL },
      { label: 'Last name', fieldName: 'lastName', fieldType: EAdminPageFieldType.LABEL },
      { label: 'Display name', fieldName: 'displayName', fieldType: EAdminPageFieldType.LABEL },
      { label: 'Is account created', fieldName: 'isAccountCreated', fieldType: EAdminPageFieldType.LABEL },
      { label: 'Row version', fieldName: 'rowVersion', fieldType: EAdminPageFieldType.LABEL },
      { label: '', fieldName: '', fieldType: EAdminPageFieldType.SEPARATOR },
    ],
  },
];

export const globalRolesConfig: Array<IAdminPageFieldConfig | IAdminPageFieldConfigSelect> = [
  { label: 'ID', fieldName: 'userProfile.id', fieldType: EAdminPageFieldType.LABEL },
  { label: 'Display Name', fieldName: 'userProfile.displayName', fieldType: EAdminPageFieldType.LABEL },
  { label: 'First Name', fieldName: 'userProfile.firstName', fieldType: EAdminPageFieldType.LABEL },
  { label: 'Last Name', fieldName: 'userProfile.lastName', fieldType: EAdminPageFieldType.LABEL },
  { label: 'Email', fieldName: 'userProfile.email', fieldType: EAdminPageFieldType.LABEL },
  { label: 'Fax', fieldName: 'userProfile.fax', fieldType: EAdminPageFieldType.LABEL },
  { label: 'Is Locked', fieldName: 'userProfile.isLocked', fieldType: EAdminPageFieldType.LABEL },
  { label: 'UID', fieldName: 'userProfile.uid', fieldType: EAdminPageFieldType.LABEL },
  { label: 'Upn', fieldName: 'userProfile.upn', fieldType: EAdminPageFieldType.LABEL },
  { label: 'Row Version', fieldName: 'userProfile.rowVersion', fieldType: EAdminPageFieldType.LABEL },
  {
    label: 'Roles',
    fieldName: 'roles',
    fieldType: EAdminPageFieldType.CHECKBOX_GROUP,
    optionGetProp: { shouldConvertOptions: true },
    optionGetFn: useFetchGlobalRoleOptions as ({
      isDirty,
      shouldConvertOptions,
    }: IUseFetchOptionsParams) => () => UseQueryResult<IOption<IRoleOption>[], AxiosErrorResponse>,
    gridSm: 12,
  },
];

export const teamInfoConfig: IAdminPageFieldConfig[] = [
  { label: 'ID', fieldName: 'id', fieldType: EAdminPageFieldType.LABEL },
  { label: 'Project Id', fieldName: 'projectId', fieldType: EAdminPageFieldType.LABEL },
  { label: 'Project Company Id', fieldName: 'projectCompanyId', fieldType: EAdminPageFieldType.LABEL },
  { label: 'Team Name', fieldName: 'teamName', fieldType: EAdminPageFieldType.TEXT_INPUT },
  { label: 'Is Default', fieldName: 'isDefault', fieldType: EAdminPageFieldType.BOOLEAN },
  { label: 'Row Version', fieldName: 'rowVersion', fieldType: EAdminPageFieldType.LABEL },
];

export const iceCertConfig: IAdminPageFieldConfig[] = [
  { label: 'Project Id', fieldName: 'projectId', fieldType: EAdminPageFieldType.LABEL },
  { label: 'Masterschedule Id', fieldName: 'masterScheduleId', fieldType: EAdminPageFieldType.LABEL },
  { label: 'Transmittal Id', fieldName: 'transmittalId', fieldType: EAdminPageFieldType.LABEL },
  { label: 'File path', fieldName: 'projectFile.url', fieldType: EAdminPageFieldType.LABEL },
  { label: 'Row Version', fieldName: 'rowVersion', fieldType: EAdminPageFieldType.LABEL },
];

export enum EAdminUploadFileFormKey {
  FILE_CONTENT = 'fileContent',
  SHOULD_OVERWRITE = 'shouldOverwrite',
}

export const uploadFileConfig: IAdminPageFieldConfig[] = [
  {
    label: 'File',
    fieldName: EAdminUploadFileFormKey.FILE_CONTENT,
    fieldType: EAdminPageFieldType.FILE_UPLOAD_SINGLE,
    acceptFileTypes: [FileTypes.Image, FileTypes.Video],
  },
  {
    label: 'Should overwrite existing file?',
    fieldName: EAdminUploadFileFormKey.SHOULD_OVERWRITE,
    fieldType: EAdminPageFieldType.BOOLEAN,
  },
];

export const announcementConfig: IAdminPageFieldConfig[] = [
  {
    label: 'ID',
    fieldName: 'id',
    fieldType: {
      add: EAdminPageFieldType.NONE,
      update: EAdminPageFieldType.LABEL,
    },
  },
  { label: 'Title', fieldName: 'title', fieldType: EAdminPageFieldType.TEXT_INPUT, required: true },
  { label: 'Title Seq. No.', fieldName: 'titleSeqNo', fieldType: EAdminPageFieldType.NUMBER, required: true },
  { label: 'Title Label', fieldName: 'titleLabel', fieldType: EAdminPageFieldType.TEXT_INPUT, required: true },
  { label: '', fieldName: '', fieldType: EAdminPageFieldType.SEPARATOR },
  { label: 'Text1 Label', fieldName: 'text1Label', fieldType: EAdminPageFieldType.TEXT_INPUT },
  { label: 'Text1 Seq. No.', fieldName: 'text1SeqNo', fieldType: EAdminPageFieldType.NUMBER },
  { label: 'Text1', fieldName: 'text1', fieldType: EAdminPageFieldType.TEXT_AREA },
  { label: '', fieldName: '', fieldType: EAdminPageFieldType.SEPARATOR },
  { label: 'Text2 Label', fieldName: 'text2Label', fieldType: EAdminPageFieldType.TEXT_INPUT },
  { label: 'Text2 Seq. No.', fieldName: 'text2SeqNo', fieldType: EAdminPageFieldType.NUMBER },
  { label: 'Text2', fieldName: 'text2', fieldType: EAdminPageFieldType.TEXT_AREA },
  { label: '', fieldName: '', fieldType: EAdminPageFieldType.SEPARATOR },
  { label: 'Text3 Label', fieldName: 'text3Label', fieldType: EAdminPageFieldType.TEXT_INPUT },
  { label: 'Text3 Seq. No.', fieldName: 'text3SeqNo', fieldType: EAdminPageFieldType.NUMBER },
  { label: 'Text3', fieldName: 'text3', fieldType: EAdminPageFieldType.TEXT_AREA },
  { label: '', fieldName: '', fieldType: EAdminPageFieldType.SEPARATOR },
  { label: 'Text4 Label', fieldName: 'text4Label', fieldType: EAdminPageFieldType.TEXT_INPUT },
  { label: 'Text4 Seq. No.', fieldName: 'text4SeqNo', fieldType: EAdminPageFieldType.NUMBER },
  { label: 'Text4', fieldName: 'text4', fieldType: EAdminPageFieldType.TEXT_AREA },
  { label: '', fieldName: '', fieldType: EAdminPageFieldType.SEPARATOR },
  { label: 'Media1 Label', fieldName: 'media1Label', fieldType: EAdminPageFieldType.TEXT_INPUT },
  { label: 'Media1 Url', fieldName: 'media1Url', fieldType: EAdminPageFieldType.TEXT_INPUT },
  { label: 'Media1 Seq. No.', fieldName: 'media1SeqNo', fieldType: EAdminPageFieldType.NUMBER },
  { label: 'Media1 Group Id', fieldName: 'media1GroupId', fieldType: EAdminPageFieldType.NUMBER },
  { label: '', fieldName: '', fieldType: EAdminPageFieldType.SEPARATOR },
  { label: 'Media2 Label', fieldName: 'media2Label', fieldType: EAdminPageFieldType.TEXT_INPUT },
  { label: 'Media2 Url', fieldName: 'media2Url', fieldType: EAdminPageFieldType.TEXT_INPUT },
  { label: 'Media2 Seq. No.', fieldName: 'media2SeqNo', fieldType: EAdminPageFieldType.NUMBER },
  { label: 'Media2 Group Id', fieldName: 'media2GroupId', fieldType: EAdminPageFieldType.NUMBER },
  { label: '', fieldName: '', fieldType: EAdminPageFieldType.SEPARATOR },
  { label: 'Media3 Label', fieldName: 'media3Label', fieldType: EAdminPageFieldType.TEXT_INPUT },
  { label: 'Media3 Url', fieldName: 'media3Url', fieldType: EAdminPageFieldType.TEXT_INPUT },
  { label: 'Media3 Seq. No.', fieldName: 'media3SeqNo', fieldType: EAdminPageFieldType.NUMBER },
  { label: 'Media3 Group Id', fieldName: 'media3GroupId', fieldType: EAdminPageFieldType.NUMBER },
  { label: '', fieldName: '', fieldType: EAdminPageFieldType.SEPARATOR },
  { label: 'Media4 Label', fieldName: 'media4Label', fieldType: EAdminPageFieldType.TEXT_INPUT },
  { label: 'Media4 Url', fieldName: 'media4Url', fieldType: EAdminPageFieldType.TEXT_INPUT },
  { label: 'Media4 Seq. No.', fieldName: 'media4SeqNo', fieldType: EAdminPageFieldType.NUMBER },
  { label: 'Media4 Group Id', fieldName: 'media4GroupId', fieldType: EAdminPageFieldType.NUMBER },
  { label: '', fieldName: '', fieldType: EAdminPageFieldType.SEPARATOR },
  { label: 'Media5 Label', fieldName: 'media5Label', fieldType: EAdminPageFieldType.TEXT_INPUT },
  { label: 'Media5 Url', fieldName: 'media5Url', fieldType: EAdminPageFieldType.TEXT_INPUT },
  { label: 'Media5 Seq. No.', fieldName: 'media5SeqNo', fieldType: EAdminPageFieldType.NUMBER },
  { label: 'Media5 Group Id', fieldName: 'media5GroupId', fieldType: EAdminPageFieldType.NUMBER },
  { label: '', fieldName: '', fieldType: EAdminPageFieldType.SEPARATOR },
  {
    label: 'Effective Start Date Time',
    fieldName: 'effectiveStartDateTime',
    fieldType: EAdminPageFieldType.DATE,
    required: true,
  },
  { label: 'Effective End Date Time', fieldName: 'effectiveEndDateTime', fieldType: EAdminPageFieldType.DATE },
  {
    label: 'Created Date Time',
    fieldName: 'createdDateTime',
    fieldType: { update: EAdminPageFieldType.LABEL_DATE, add: EAdminPageFieldType.NONE },
  },
  {
    label: 'Last Modified Date Time',
    fieldName: 'lastModifiedDateTime',
    fieldType: { update: EAdminPageFieldType.LABEL_DATE, add: EAdminPageFieldType.NONE },
  },
  {
    label: 'Last Modified By',
    fieldName: 'lastModifiedBy',
    fieldType: {
      add: EAdminPageFieldType.NONE,
      update: EAdminPageFieldType.LABEL,
    },
  },
  {
    label: 'Row Version',
    fieldName: 'rowVersion',
    fieldType: {
      add: EAdminPageFieldType.NONE,
      update: EAdminPageFieldType.LABEL,
    },
  },
];

export const adminPageList: IAdminPageItemConfig<any, any>[] = [
  {
    pageName: 'Team Info',
    pageType: EAdminPageType.TEAM_INFO,
    fieldConfigs: teamInfoConfig,
    enableUpdateWithId: true,
    fetchFn: useFetchAdminTeamInfo,
    updateFn: useUpdateAdminTeamInfo,
    fetchQueryKey: FETCH_QUERIES_NAME.ADMIN_TEAM_INFO,
    idFieldLabel: 'Team ID',
  },
  {
    pageName: 'Upload Announcement File',
    pageType: EAdminPageType.UPLOAD_FILE,
    fieldConfigs: uploadFileConfig,
    enableUpdateWithId: false,
    enableAdd: true,
    addFn: useAnnouncementFileUpload,
    addSuccessCallback: uploadFileSuccessCallback,
  },
  {
    pageName: 'Announcement',
    pageType: EAdminPageType.ANNOUNCEMENT,
    fieldConfigs: announcementConfig,
    enableUpdateWithId: true,
    fetchFn: useFetchAnnouncement,
    updateFn: useUpdateAnnouncement,
    enableAdd: true,
    addFn: useCreateAnnouncement,
    fetchQueryKey: FETCH_QUERIES_NAME.ANNOUNCEMENT_DETAIL,
    idFieldLabel: 'Announcement ID',
  },
  {
    pageName: 'Global Roles',
    pageType: EAdminPageType.GLOBAL_ROLES,
    fieldConfigs: globalRolesConfig,
    enableUpdateWithId: true,
    updateFn: useUpdateGlobalRoles,
    enableAdd: false,
    fetchQueryKey: FETCH_QUERIES_NAME.GLOBAL_ROLES_BY_USER_PROFILE_ID,
    fetchFn: useFetchGlobalRolesByUserProfileId,
    idFieldLabel: 'User Profile ID',
  },
  {
    pageName: 'Sync No Account Users by ProjectNo',
    pageType: EAdminPageType.SYNC_NO_ACCOUNT_USERS,
    fieldConfigs: syncNoAccountUsersConfig,
    enableAdd: false,
    fetchMutationFn: useUpdateUserProfilesSyncNoAccountUsersByProjectNo,
    enableUpdateWithId: true,
    idFieldLabel: 'Project No.',
    idFieldFormKey: EAdminPageMainFormKey.PROJECT_NO,
  },
  {
    pageName: 'User Profile',
    pageType: EAdminPageType.USER_PROFILE,
    fieldConfigs: userProfileConfig,
    enableUpdateWithId: true,
    fetchFn: useFetchUserProfile,
    updateFn: useUpdateUserProfile,
    fetchQueryKey: FETCH_QUERIES_NAME.USER_PROFILE,
    enableAdd: false,
    idFieldLabel: 'User Profile ID',
  },
  {
    pageName: 'Training Record',
    pageType: EAdminPageType.TRAINING_RECORD,
    fieldConfigs: addOrUpdateTrainingRecordConfig,
    enableUpdateWithId: true,
    fetchFn: useFetchTraining,
    updateFn: useUpdateTraining,
    addFn: useCreateTraining,
    fetchQueryKey: FETCH_QUERIES_NAME.TRAINING,
    enableAdd: true,
    idFieldLabel: 'Training ID',
    headerComponent: <AdminPageSyncTrainingRecordButton />,
  },
  {
    pageName: 'ICE Cert',
    pageType: EAdminPageType.ICE_CERT,
    fieldConfigs: iceCertConfig,
    enableUpdateWithId: true,
    fetchFn: useFetchICECert,
    updateFn: useUpdateICECert,
    fetchQueryKey: FETCH_QUERIES_NAME.ICE_CERT,
    idFieldLabel: 'Id',
  },
  {
    pageName: 'Update User Roles',
    pageType: EAdminPageType.PROJECT_USER_PROFILE_WITH_ROLES_BY_COMPANY,
    fieldConfigs: updateProjectUserRoleConfig,
    fetchFormExtraFields: updateProjectUserRoleFetchFormConfig,
    enableUpdateWithId: true,
    fetchFn: useFetchProjectUserProfileByCompany,
    fetchQueryKey: FETCH_QUERIES_NAME.ADMIN_PROJECT_USER_PROFILE_WITH_ROLES_BY_COMPANY,
    updateFn: useAdminUpdateProjectUserProfileAsync,
    idFieldLabel: 'User Profile ID',
  },
  {
    pageName: 'Update Dummy Accounts',
    pageType: EAdminPageType.UPDATE_DUMMY_USER_PROFILES,
    fieldConfigs: updateDummyUserProfilesFormConfig,
    enableUpdateWithId: true,
    fetchFn: useFetchUserProfile,
    enableAdd: false,
    idFieldLabel: 'User Profile ID',
    updateFn: useReplaceDummyUserProfiles,
  },
  {
    pageName: 'Update Signature',
    pageType: EAdminPageType.SIGNATURE,
    fieldConfigs: updateSignatureConfig,
    enableUpdateWithId: true,
    fetchFn: useFetchAdminSignature,
    fetchQueryKey: FETCH_QUERIES_NAME.ADMIN_SIGNATURE,
    updateFn: useUpdateAdminSignature,
    dropFields: ['profileId', 'projectId', 'mime'],
    enableAdd: true,
    addFn: usePostAdminSignature,
    idFieldLabel: 'Signature ID',
  },
  {
    pageName: 'Role',
    pageType: EAdminPageType.ROLE,
    fieldConfigs: addRoleFormConfig,
    enableAdd: true,
    enableUpdateWithId: false,
    addFn: useCreateRole,
  },
  {
    pageName: 'Project Role',
    pageType: EAdminPageType.PROJECT_ROLE,
    fieldConfigs: addProjectRoleFormConfig,
    enableAdd: true,
    enableUpdateWithId: false,
    addFn: useCreateProjectRole,
  },
  {
    pageName: 'Permission',
    pageType: EAdminPageType.PERMISSION,
    fieldConfigs: addPermissionFormConfig,
    enableAdd: true,
    enableUpdateWithId: false,
    addFn: useCreatePermission,
  },
  {
    pageName: 'Role Permission',
    pageType: EAdminPageType.ROLE_PERMISSION,
    fieldConfigs: addRolePermissionFormConfig,
    enableAdd: true,
    enableUpdateWithId: false,
    addFn: useCreateRolePermission,
  },
];
